import { z } from 'zod';
import { LEAD_TIME_SOURCES, type LeadTimeSource } from '@erp/shared-types';
import type {
  DemandComparison,
  DemandMatrixFilter,
  GetDemandMatrixArgs,
} from 'src/api/rpc/hooks/procurement/useDemandForecast';

/**
 * The page's whole state lives in the URL. That is what makes a filtered view
 * something a buyer can send to a colleague, and it is also why the filter bar
 * needs no local state of its own — the search params are the state.
 *
 * Every field is optional and absent when unset, so a default view has a clean
 * URL and `Object.keys` on the parsed search doubles as "which filters are on".
 */
export const demandSearchSchema = z.object({
  runId: z.uuid().optional(),
  divisionIds: z.array(z.uuid()).optional(),
  warehouseIds: z.array(z.uuid()).optional(),
  warehouseTypes: z.array(z.string()).optional(),
  skuKeys: z.array(z.string()).optional(),
  skuSearch: z.string().optional(),
  textSearch: z.string().optional(),
  demandClasses: z.array(z.string()).optional(),
  methods: z.array(z.string()).optional(),
  leadTimeSources: z.array(z.string()).optional(),
  stockStatuses: z.array(z.string()).optional(),
  popularityCodes: z.array(z.string()).optional(),
  overrideOnly: z.boolean().optional(),
  monthFrom: z.string().optional(),
  monthTo: z.string().optional(),
  rollup: z.enum(['warehouse', 'division', 'sku']).optional(),
  measure: z.enum(['quantity', 'cost']).optional(),
  /** 'actuals', or a run id to compare this run's curve against. */
  compare: z.array(z.string()).optional(),
  /** Prepend the previous period of the same length. Needs 'actuals' to mean anything. */
  history: z.boolean().optional(),
  /** Drop rows the run forecast at zero across every visible month. */
  hideEmpty: z.boolean().optional(),
  /**
   * Show each comparison's difference from the forecast, coloured, beside it.
   * Off by default so a compared month reads as plain numbers.
   */
  showDiff: z.boolean().optional(),
  /**
   * Show the open month's whole forecast. Absent, its forecast line runs to
   * today, like the month-to-date actual under it — where the run has a daily
   * forecast to sum.
   */
  fullMonth: z.boolean().optional(),
  sortBy: z.enum(['location', 'total', 'month']).optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
  sortMonth: z.number().int().min(0).optional(),
  page: z.number().int().min(1).optional(),
  /**
   * How much room the summary takes. Absent is 'collapsed': a rail on the right
   * edge, which also skips the aggregate query. 'standard' is the quarter-width
   * panel beside the filters; 'full' is its own row above the grid.
   */
  summary: z.enum(['standard', 'full']).optional(),
});

export type DemandSearch = z.infer<typeof demandSearchSchema>;

/**
 * The detail route's search: every filter the grid carries, plus the item the
 * page is about.
 *
 * Sharing the grid's schema rather than declaring a fresh one is what makes the
 * two routes interchangeable — opening an item keeps the run, the month range
 * and every filter behind it, and going back restores the grid exactly.
 *
 * `sku` is the only extra key, and the only required one. The location is NOT
 * an extra: the detail page aggregates over `divisionIds` / `warehouseIds`, the
 * same two keys the grid filters by, so narrowing to one branch and going back
 * carries that narrowing with it instead of discarding it at the boundary.
 */
export const demandDetailSearchSchema = demandSearchSchema.extend({
  /** `im_products_v3.key`. */
  sku: z.string().optional(),
  /** Zero-based row in the filtered grid, used for bounded record navigation. */
  rowIndex: z.number().int().nonnegative().optional(),
  /** Freeze the grid's resolved run without changing its Latest selection. */
  navigationRunId: z.uuid().optional(),
  /**
   * The item's own location, when the page is about one row of the grid.
   * Kept apart from `warehouseIds` on purpose: those are the shared procurement
   * filters (the list the buyer is working through), this is where the page
   * sits. Absent, the page sums the item over the filters' scope.
   */
  warehouseId: z.uuid().optional(),
  /**
   * The division a SKU × division row sits in. Like `warehouseId` it is where
   * the page sits, not a narrowing of the list's filters; the page sums the
   * item over that division's locations.
   */
  divisionId: z.uuid().optional(),
});

export type DemandDetailSearch = z.infer<typeof demandDetailSearchSchema>;

/**
 * The grid search a detail page belongs to: what Previous/Next walks, what the
 * record picker searches, and what "back" returns to. The filters are already
 * the list's, so this only drops what identifies the item.
 */
export function listSearchOf(search: DemandDetailSearch): DemandSearch {
  const {
    sku: _sku,
    rowIndex: _rowIndex,
    navigationRunId: _navigationRunId,
    warehouseId: _warehouseId,
    divisionId: _divisionId,
    ...list
  } = search;
  return list;
}

/**
 * The locations the detail page sums over: the item's own when it has one,
 * otherwise the filters' scope.
 */
export function detailScopeOf(search: DemandDetailSearch): {
  divisionIds: string[] | undefined;
  warehouseIds: string[] | undefined;
} {
  if (search.warehouseId) {
    return { divisionIds: undefined, warehouseIds: [search.warehouseId] };
  }
  if (search.divisionId) {
    return { divisionIds: [search.divisionId], warehouseIds: undefined };
  }
  return { divisionIds: search.divisionIds, warehouseIds: search.warehouseIds };
}

/**
 * An edit to the detail page's location controls.
 *
 * Those controls show `detailScopeOf` — the scope the page is summed over,
 * the row's own location included — never the list's filters alone, which
 * read "Any" while the page sat on one branch (GEN-3236). So an edit replaces
 * that scope: it becomes the list's filters, and the page leaves its row.
 */
export function withDetailScope(
  search: DemandDetailSearch,
  scope: {
    divisionIds: string[] | undefined;
    warehouseIds: string[] | undefined;
  },
): DemandDetailSearch {
  return {
    ...search,
    ...scope,
    warehouseId: undefined,
    divisionId: undefined,
  };
}

/**
 * A location filter changed on the detail page: the buyer is choosing where to
 * look, so the page follows the new scope rather than staying on one row, and
 * record navigation ends — the list it was walking is not this one.
 */
export function withDetailFilters(
  current: DemandDetailSearch,
  next: DemandDetailSearch,
): DemandDetailSearch {
  // Either the list's filters moved or the page left its row: both change
  // where the page is summed.
  const scopeChanged =
    JSON.stringify([current.divisionIds, current.warehouseIds]) !==
      JSON.stringify([next.divisionIds, next.warehouseIds]) ||
    JSON.stringify(detailScopeOf(current)) !==
      JSON.stringify(detailScopeOf(next));
  if (!scopeChanged) return next;
  return {
    ...next,
    warehouseId: undefined,
    divisionId: undefined,
    rowIndex: undefined,
    navigationRunId: undefined,
  };
}

/** Shared by the grid and record navigation so ordering and month offsets agree. */
export function toDemandMatrixInput(
  search: DemandSearch,
): Omit<GetDemandMatrixArgs, 'page'> {
  const comparisons = toComparisons(search);
  return {
    ...toMatrixFilter(search),
    rollup: search.rollup ?? 'warehouse',
    measure: search.measure ?? 'quantity',
    comparisons,
    includeHistory:
      search.history === true &&
      comparisons.some((entry) => entry.kind === 'actuals'),
    sortBy: search.sortBy ?? 'location',
    sortDir: search.sortDir ?? 'asc',
    sortMonth: search.sortMonth,
    pageSize: DEMAND_PAGE_SIZE,
  };
}

/** The summary's three widths, as one value rather than two booleans. */
export type SummaryState = 'collapsed' | 'standard' | 'full';

export function summaryStateOf(search: DemandSearch): SummaryState {
  return search.summary ?? 'collapsed';
}

/**
 * Every filter the bar can add, in menu order. One table drives the menu, the
 * chips and the reset logic, so a new dimension is one entry rather than four
 * parallel edits.
 */
export const FILTER_KEYS = [
  'divisionIds',
  'warehouseIds',
  'warehouseTypes',
  'skuSearch',
  'textSearch',
  'stockStatuses',
  'popularityCodes',
  'demandClasses',
  'methods',
  'leadTimeSources',
  'overrideOnly',
  'monthRange',
] as const;

export type FilterKey = (typeof FILTER_KEYS)[number];

/**
 * Filters pinned to the bar before anyone changes anything. These three are the
 * dimensions the page exists to slice by, so making them addable-but-hidden
 * just added a click to every session. A user's own pin set overrides this and
 * follows them across sessions and devices.
 */
export const DEFAULT_FILTER_KEYS: FilterKey[] = [
  'skuSearch',
  'divisionIds',
  'warehouseIds',
];

/** Stored pin set. A plain array so an unknown key from an older build drops. */
export const PinnedFiltersSchema = z.object({
  pinned: z.array(z.enum(FILTER_KEYS)).optional(),
});

/** Preference row id — see useTableFilterPreferences. */
export const PINNED_FILTERS_TABLE_ID = 'procurement:demand-grid:pinned';

/**
 * `compare` entries are either the literal 'actuals' or a run id. Narrowed to
 * the contract's discriminated union here rather than cast, so an entry that is
 * neither cannot reach the request.
 */
export function toComparisons(
  search: DemandSearch,
  activeRunId = search.runId,
): DemandComparison[] {
  const out: DemandComparison[] = [];
  for (const entry of new Set(search.compare ?? [])) {
    if (entry === 'actuals') {
      out.push({ kind: 'actuals' });
    } else if (entry !== activeRunId && UUID_PATTERN.test(entry)) {
      out.push({ kind: 'run', runId: entry });
    }
  }
  return out;
}

/** History needs Actual and the matrix accepts at most three comparisons. */
export function withHistory(
  search: DemandSearch,
  enabled: boolean,
): DemandSearch {
  if (!enabled) return { ...search, history: undefined };
  const other: string[] = [];
  for (const entry of toComparisons(search)) {
    if (entry.kind !== 'run') continue;
    other.push(entry.runId);
    if (other.length === 2) break;
  }
  return {
    ...search,
    history: true,
    compare: [...other, 'actuals'],
  };
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Filters whose value is free text, so the bar knows to keep a draft for them. */
export const TEXT_FILTER_KEYS = ['skuSearch', 'textSearch'] as const;
export type TextFilterKey = (typeof TEXT_FILTER_KEYS)[number];

/** Search-param fields each filter owns, for adding and clearing it wholesale. */
const FILTER_FIELDS: Record<FilterKey, (keyof DemandSearch)[]> = {
  divisionIds: ['divisionIds'],
  warehouseIds: ['warehouseIds'],
  warehouseTypes: ['warehouseTypes'],
  skuSearch: ['skuSearch'],
  textSearch: ['textSearch'],
  stockStatuses: ['stockStatuses'],
  popularityCodes: ['popularityCodes'],
  demandClasses: ['demandClasses'],
  methods: ['methods'],
  leadTimeSources: ['leadTimeSources'],
  overrideOnly: ['overrideOnly'],
  monthRange: ['monthFrom', 'monthTo'],
};

/**
 * A filter is "shown" once it has a value, and stays shown while the user is
 * still choosing one. That pending set is the only piece of state the bar keeps
 * outside the URL: an empty multi-select would otherwise vanish the moment it
 * was added.
 */
export function activeFilterKeys(search: DemandSearch): FilterKey[] {
  return FILTER_KEYS.filter((key) =>
    FILTER_FIELDS[key].some((field) => search[field] !== undefined),
  );
}

/**
 * The filter fields alone, without display, sort, paging or run. This is what a
 * buyer means by "my filters", so it is the part that follows them from screen
 * to screen (see procurement/filters). Picked from the grid's own schema
 * so the types agree; a test pins the mask to FILTER_FIELDS so a new filter
 * cannot be clearable yet forgotten on the next screen.
 */
export const demandFilterFieldsSchema = demandSearchSchema.pick({
  divisionIds: true,
  warehouseIds: true,
  warehouseTypes: true,
  skuSearch: true,
  textSearch: true,
  stockStatuses: true,
  popularityCodes: true,
  demandClasses: true,
  methods: true,
  leadTimeSources: true,
  overrideOnly: true,
  monthFrom: true,
  monthTo: true,
});

export type DemandFilterFields = z.infer<typeof demandFilterFieldsSchema>;

const FILTER_FIELD_NAMES = demandFilterFieldsSchema.keyof().options;

/**
 * Just the filter fields of a search. A pick, not a parse: the route has
 * already validated the search, and a filter this cannot vouch for is still
 * the filter on screen.
 */
export function filterFieldsOf(search: DemandSearch): DemandFilterFields {
  const out: DemandFilterFields = {};
  for (const field of FILTER_FIELD_NAMES) copyField(out, search, field);
  return out;
}

function copyField<K extends keyof DemandFilterFields>(
  out: DemandFilterFields,
  from: DemandSearch,
  key: K,
): void {
  if (from[key] !== undefined) out[key] = from[key];
}

export function clearFilter(
  search: DemandSearch,
  key: FilterKey,
): DemandSearch {
  // Set to undefined rather than deleted: the procurement filter route keeps
  // any filter a navigation leaves out, so leaving one out is not a clear.
  const next = { ...search };
  for (const field of FILTER_FIELDS[key]) next[field] = undefined;
  return next;
}

/** Clear every filter, or just the ones a screen offers. */
export function clearAllFilters(
  search: DemandSearch,
  keys: readonly FilterKey[] = FILTER_KEYS,
): DemandSearch {
  let next = { ...search };
  for (const key of keys) next = clearFilter(next, key);
  return next;
}

/** The subset of the URL the API actually filters on. */
export function toMatrixFilter(search: DemandSearch): DemandMatrixFilter {
  return {
    runId: search.runId,
    divisionIds: search.divisionIds,
    warehouseIds: search.warehouseIds,
    warehouseTypes: search.warehouseTypes,
    skuKeys: search.skuKeys,
    skuSearch: search.skuSearch || undefined,
    textSearch: search.textSearch || undefined,
    demandClasses: search.demandClasses,
    methods: search.methods,
    leadTimeSources: search.leadTimeSources?.filter(isLeadTimeSource),
    stockStatuses: search.stockStatuses,
    popularityCodes: search.popularityCodes,
    overrideOnly: search.overrideOnly,
    monthFrom: search.monthFrom,
    monthTo: search.monthTo,
    hideEmpty: search.hideEmpty,
  };
}

/**
 * The URL carries lead-time sources as free strings — anyone can type one — so
 * they are narrowed here rather than cast. An unknown value is dropped, which
 * widens the result set; sending it on would fail the contract instead. The
 * accepted list is the shared one the contract's enum derives from.
 */
function isLeadTimeSource(value: string): value is LeadTimeSource {
  return (LEAD_TIME_SOURCES as readonly string[]).includes(value);
}

/**
 * Rows per grid page. Lives here rather than in the table component so the page
 * can pass it to the query without importing a non-component from a component
 * file (react-doctor/only-export-components).
 */
export const DEMAND_PAGE_SIZE = 50;

/**
 * A run's identity as people refer to it: the date it ran, plus enough time to
 * tell two runs on the same day apart.
 */
export function runLabel(startedAt: string): string {
  if (!startedAt) return '—';
  // One zone for both halves. Formatting the date in UTC and the time locally
  // made them describe different days for a run started near midnight UTC:
  // 2026-01-01T02:00Z in UTC-5 rendered as '2026-01-01 21:00', an instant that
  // is actually 21:00 on 2025-12-31.
  return new Date(startedAt).toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Short month names, January first — index is month number minus one.
 *
 * Built from `Intl` rather than a hard-coded English list or a translation key.
 * Month abbreviations are exactly what `Intl` exists to produce, it already
 * knows every locale's, and unlike `gt()` it works at module scope where these
 * are needed. The reader's own locale decides; a UTC time zone keeps the
 * formatter from rolling a month backwards for readers west of Greenwich.
 */
const MONTH_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  timeZone: 'UTC',
});

export const MONTH_NAMES: string[] = Array.from({ length: 12 }, (_, index) =>
  MONTH_FORMAT.format(Date.UTC(2000, index, 1)),
);

/** 'YYYY-MM-01' → 'Oct 26', the column header. */
export function formatMonthLabel(month: string): string {
  const [year, monthNumber] = month.split('-');
  return `${MONTH_NAMES[Number(monthNumber) - 1] ?? monthNumber} ${year.slice(2)}`;
}

/**
 * Quantities in full, with separators and no decimals.
 *
 * NOT abbreviated. A grid is read to get a number out of it, and "12k" is not a
 * number — it is a range four hundred units wide. Columns get wider; that is
 * the correct trade.
 */
export function formatQty(value: number): string {
  return Math.round(value).toLocaleString();
}

/** Money in full, to the dollar. Cents are noise at this grain. */
export function formatMoney(value: number): string {
  const rounded = Math.round(value);
  return `${rounded < 0 ? '-' : ''}$${Math.abs(rounded).toLocaleString()}`;
}

/**
 * The one place abbreviation belongs: a chart's y-axis, where the ticks are
 * scale markers rather than values anyone reads off.
 */
export function formatAxisQty(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(value / 1000)}k`;
  return String(Math.round(value));
}

/**
 * First month the grid treats as future, as YYYY-MM-01.
 *
 * The current month counts as present, not future: it is partly lived and its
 * actual so far is a real number. Only months that have not started at all are
 * excluded.
 */
export function firstFutureMonthFrom(now: Date): string {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + 1; // 0-indexed, and we want the NEXT month
  const rolled = month > 11 ? { y: year + 1, m: 1 } : { y: year, m: month + 1 };
  return `${rolled.y}-${String(rolled.m).padStart(2, '0')}-01`;
}

/**
 * Which comparisons have anything to say about one month, as indices into the
 * requested list.
 *
 * Shared by the header and the cells so they cannot disagree: when they each
 * decided independently, the header labelled every month 'Fcst | Actual' while
 * the cells only split some, and nothing lined up.
 */
export function applicableComparisons(
  month: string,
  comparisonKinds: readonly ('actuals' | 'run')[],
  firstFutureMonth: string,
): number[] {
  const indices: number[] = [];
  comparisonKinds.forEach((kind, index) => {
    // A month nobody has started cannot have an actual.
    if (kind === 'actuals' && month >= firstFutureMonth) return;
    indices.push(index);
  });
  return indices;
}

/**
 * The forecast on the open month's first line: to date when that is chosen and
 * the row has a daily forecast to sum, else the whole month. A monthly forecast
 * is never spread evenly over its days to fake one.
 */
export function openMonthForecast(
  row: { forecastToDate?: number | null },
  qty: number | null,
  fullMonth: boolean,
): { value: number | null; toDate: boolean } {
  if (fullMonth || row.forecastToDate == null)
    return { value: qty, toDate: false };
  return { value: row.forecastToDate, toDate: true };
}

/**
 * A month cell's forecast line: the number it stands for and its text. Blank
 * on a history month (the run held no forecast for a month already past) and
 * for an unpriced SKU's cost, which is unknown rather than zero.
 */
export function forecastLine(
  row: { forecastToDate?: number | null },
  qty: number | null,
  options: {
    isOpenMonth: boolean;
    fullMonth: boolean;
    blankCost: boolean;
    measure: DemandSearch['measure'];
  },
): { shown: { value: number | null; toDate: boolean }; text: string } {
  const shown = options.isOpenMonth
    ? openMonthForecast(row, qty, options.fullMonth)
    : { value: qty, toDate: false };
  const text =
    shown.value === null || options.blankCost
      ? ''
      : formatMeasure(shown.value, options.measure);
  return { shown, text };
}

/**
 * Whether the grid can offer a forecast to date: hidden with no month-to-date
 * on screen, offered but greyed when no row on it has a daily forecast.
 */
export function forecastToDateState(
  pages: readonly {
    monthToDate?: { month: string } | null;
    rows: readonly { forecastToDate?: number | null }[];
  }[],
): 'available' | 'unavailable' | undefined {
  if (!pages.some((page) => page.monthToDate)) return undefined;
  return pages.some((page) =>
    page.rows.some((row) => row.forecastToDate != null),
  )
    ? 'available'
    : 'unavailable';
}

/**
 * Colour of a comparison's difference. The open month is still being lived
 * through, so its difference stays neutral until the month closes.
 */
export function varianceTone(diff: number, partial: boolean): string {
  if (partial) return 'text-muted-foreground';
  return diff > 0 ? 'text-emerald-700' : 'text-red-700';
}

/** Cells switch between the two formatters; nothing else changes. */
export function formatMeasure(
  value: number,
  measure: DemandSearch['measure'],
): string {
  return measure === 'cost' ? formatMoney(value) : formatQty(value);
}

/**
 * Selected ids back to options, for any multi-select on either filter bar.
 *
 * An id with no matching option still renders as itself: a URL can name a
 * division that has since been renamed away, and dropping it silently would
 * change the scope the buyer is looking at without saying so.
 */
export function selectedOptions<T extends { id: string; label: string }>(
  values: string[] | undefined,
  all: T[],
): (T | { id: string; label: string })[] {
  return (values ?? []).map(
    (id) => all.find((option) => option.id === id) ?? { id, label: id },
  );
}

/** An empty selection is no filter at all, not a filter matching nothing. */
export function idsOrUndefined(next: { id: string }[]): string[] | undefined {
  return next.length ? next.map((option) => option.id) : undefined;
}
